import { arrayMove } from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { get, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MoneyInput, SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import type { Attribute, Product, Variant } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';

import { useProductType } from '@/features/dictionary';
import { BASE_CURRENCY } from '@/shared/catalog/base-currency';

import { combinationCount } from '../api/mappers';
import { previewOptionChange, syncBasePrice } from '../api/option-sync-preview';
import { useProductScope } from '../hooks/use-product-scope';
import type { OptionAxisForm, ProductFormInput } from '../schemas/product-form-schema';

import { OptionRow, StaticValueChip } from './option-row';
import { SortableOptionList } from './option-sortable';
import { SharedChip } from './product-scope';

export const MAX_OPTION_AXES = 6;

export interface OptionsSectionProps {
  attributes: readonly Attribute[];
  attributesById: ReadonlyMap<string, Attribute>;
  /** The saved product, to say what saving would change; undefined on the create page. */
  saved?: Pick<Product, 'id' | 'options'> | undefined;
  /** The saved product's variants, for the same; undefined while they load. */
  variants?: readonly Variant[] | undefined;
}

/** What saving would do, in words. Nothing while the options are the saved ones. */
function SaveSummary({
  saved,
  options,
  attributesById,
  variants,
}: {
  saved: Pick<Product, 'options'>;
  options: readonly OptionAxisForm[];
  attributesById: ReadonlyMap<string, Attribute>;
  variants: readonly Variant[] | undefined;
}) {
  const { t } = useTranslation('products');
  const { shared, storeCount } = useProductScope();
  const format = useFormat();
  const preview = previewOptionChange(saved.options, options, attributesById, variants);
  const { counts, firstValues, removedOptions } = preview;
  const price = variants ? syncBasePrice(variants) : null;
  const notes: string[] = [];

  if (variants?.length === 0) {
    if (preview.valuesChanged) notes.push(t('optionsCard.summary.noVariants'));
  } else if (preview.valuesChanged) {
    if (!counts) {
      notes.push(t('optionsCard.summary.unknown'));
    } else if (counts.created > 0 || counts.archived > 0 || counts.before !== counts.after) {
      notes.push(
        t('optionsCard.summary.counts', {
          before: counts.before,
          after: counts.after,
          count: counts.after,
        }),
      );
    }
    if (counts && counts.created > 0) {
      notes.push(
        price
          ? t('optionsCard.summary.created', {
              count: counts.created,
              price: format.money(price),
            })
          : t('optionsCard.summary.createdNoPrice', { count: counts.created }),
      );
    }
    if (counts && counts.archived > 0) {
      notes.push(t('optionsCard.summary.archived', { count: counts.archived }));
    }
    for (const { option, value } of firstValues) {
      notes.push(t('optionsCard.summary.firstValue', { option, value }));
    }
    if (removedOptions.length > 0 && (!counts || counts.archived > 0)) {
      notes.push(t('optionsCard.summary.removedOption', { options: removedOptions.join(', ') }));
    }
  }
  if (preview.reordered) {
    notes.push(t('optionsCard.summary.order', { order: preview.order.join(', then ') }));
  }
  if (preview.renamed) notes.push(t('optionsCard.summary.renamed'));

  if (notes.length === 0) return null;
  if (shared) {
    notes.push(
      storeCount
        ? t('optionsCard.summary.allStores', { count: storeCount })
        : t('optionsCard.summary.everyStore'),
    );
  }
  return (
    <p
      role="status"
      data-testid="options-summary"
      className="border-primary/30 bg-primary-tint mt-1 rounded-lg border px-3 py-2.5 text-[13px]"
    >
      {notes.join(' ')}
    </p>
  );
}

/** The options for someone who may only read them. */
function ReadOnlyOptions({
  options,
  attributesById,
}: {
  options: readonly OptionAxisForm[];
  attributesById: ReadonlyMap<string, Attribute>;
}) {
  const { t } = useTranslation('products');
  return (
    <SummaryCard
      title={t('sections.options')}
      badge={<SharedChip />}
      description={t('masterPage.optionsReadOnly')}
    >
      {options.length > 0 ? (
        <ul className="flex flex-col" aria-label={t('sections.options')}>
          {options.map((axis) => {
            const attribute = attributesById.get(axis.attributeId);
            const title = axis.name.trim() || (attribute?.label ?? axis.attributeId);
            return (
              <li
                key={axis.attributeId}
                aria-label={title}
                className="-mx-4 flex flex-col gap-2 border-t px-4 py-3"
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{title}</span>
                  <span className="text-muted-foreground text-xs">
                    {t('optionsCard.source', { count: axis.values.length })}
                  </span>
                </div>
                <ul
                  className="flex flex-wrap gap-1.5"
                  aria-label={t('optionsCard.valuesOf', { option: title })}
                >
                  {axis.values.map((value) => (
                    <StaticValueChip
                      key={value.attributeValueId ?? value.value}
                      attribute={attribute}
                      value={value}
                    />
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      ) : null}
    </SummaryCard>
  );
}

/**
 * The choices shoppers make: which attributes define variants, in what order, under what name, and
 * which of their values are offered. Rows are edited in place and go through the save bar, and
 * saving carries the variants along (`variantSync`), which the summary under the rows spells out.
 */
export function OptionsSection({
  attributes,
  attributesById,
  saved,
  variants,
}: OptionsSectionProps) {
  const { t } = useTranslation('products');
  const { readOnly, shared, storeCount } = useProductScope();
  const form = useFormContext<ProductFormInput>();
  const options = useWatch({ control: form.control, name: 'options' });
  const productTypeId = useWatch({ control: form.control, name: 'productTypeId' });
  // Only the create page's form carries a price (`newProductForm`).
  const creating = useWatch({ control: form.control, name: 'basePrice' }) !== undefined;
  const { errors, isSubmitted } = useFormState({ control: form.control, name: 'options' });
  const productType = useProductType(!readOnly && productTypeId ? productTypeId : null);

  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());
  const [announcement, setAnnouncement] = useState('');

  // A row with an error opens, and stays open once the error is fixed.
  const erroredIds = options
    .filter((_, index) => get(errors, `options.${index}`) !== undefined)
    .map((axis) => axis.attributeId);
  const erroredKey = erroredIds.join();
  const [openedForErrors, setOpenedForErrors] = useState('');
  if (erroredKey !== openedForErrors) {
    setOpenedForErrors(erroredKey);
    if (erroredIds.length > 0) setOpenIds(new Set([...openIds, ...erroredIds]));
  }

  if (readOnly) return <ReadOnlyOptions options={options} attributesById={attributesById} />;

  const setOptions = (next: OptionAxisForm[]) =>
    form.setValue('options', next, {
      shouldDirty: true,
      // A failed save has errors on show, and fixing one should clear it.
      shouldValidate: isSubmitted,
    });
  const setOpen = (attributeId: string, open: boolean) => {
    const next = new Set(openIds);
    if (open) next.add(attributeId);
    else next.delete(attributeId);
    setOpenIds(next);
  };
  const savedAxisForm = (attributeId: string) =>
    form.formState.defaultValues?.options?.find((axis) => axis?.attributeId === attributeId) as
      OptionAxisForm | undefined;
  const addOption = (attributeId: string) => {
    // One removed and added back is the saved option again, not a new one.
    const again = savedAxisForm(attributeId);
    setOptions([
      ...options,
      again ? { ...again, values: [] } : { attributeId, name: '', values: [] },
    ]);
    setOpen(attributeId, true);
  };

  const titleOf = (axis: OptionAxisForm) =>
    axis.name.trim() || (attributesById.get(axis.attributeId)?.label ?? axis.attributeId);
  const announceMove = (name: string, position: number, count: number) =>
    setAnnouncement(t('optionsCard.dnd.dropped', { name, position, count }));
  const moveAxis = (from: number, to: number) => {
    const moved = options[from];
    setOptions(arrayMove(options, from, to));
    if (moved) announceMove(titleOf(moved), to + 1, options.length);
  };

  const used = new Set(options.map((axis) => axis.attributeId));
  const candidates = attributes.filter((a) => a.usableAsOption && !used.has(a.id));
  const room = options.length < MAX_OPTION_AXES;
  const suggestions = room
    ? (productType.data?.attributes ?? []).flatMap((row) => {
        const attribute = attributesById.get(row.attributeId);
        return attribute?.usableAsOption && !used.has(attribute.id) ? [attribute] : [];
      })
    : [];
  const optionsError =
    (get(errors, 'options.root.message') as string | undefined) ??
    (get(errors, 'options.message') as string | undefined);
  const variantsToCreate = combinationCount(options.filter((axis) => axis.values.length > 0));

  const description = creating
    ? t('optionsCard.descriptionNew')
    : shared
      ? storeCount
        ? t('optionsCard.descriptionShared', { count: storeCount })
        : t('optionsCard.descriptionSharedAny')
      : options.length === 0
        ? t('optionsCard.descriptionEmpty')
        : t('optionsCard.description');

  return (
    <SummaryCard title={t('sections.options')} badge={<SharedChip />} description={description}>
      <span aria-live="polite" className="sr-only">
        {announcement}
      </span>
      {options.length > 0 ? (
        <SortableOptionList
          ids={options.map((axis) => axis.attributeId)}
          axis="y"
          nameOf={(id) => {
            const axis = options.find((o) => o.attributeId === id);
            return axis ? titleOf(axis) : id;
          }}
          onMove={moveAxis}
        >
          <ol className="flex flex-col" aria-label={t('sections.options')}>
            {options.map((axis, index) => (
              <OptionRow
                key={axis.attributeId}
                axis={axis}
                index={index}
                count={options.length}
                attribute={attributesById.get(axis.attributeId)}
                savedAxis={saved?.options.find((o) => o.attributeId === axis.attributeId)}
                savedValues={savedAxisForm(axis.attributeId)?.values}
                markNew={saved !== undefined}
                open={openIds.has(axis.attributeId)}
                errors={errors}
                onOpen={() => setOpen(axis.attributeId, true)}
                onClose={() => setOpen(axis.attributeId, false)}
                onChange={(next) => setOptions(options.with(index, next))}
                onMove={moveAxis}
                onValueMoved={announceMove}
                onRemove={() => {
                  setOptions(options.filter((_, i) => i !== index));
                  setOpen(axis.attributeId, false);
                }}
              />
            ))}
          </ol>
        </SortableOptionList>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {options.length === 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-primary/50 bg-primary-tint text-primary border-dashed"
                disabled={candidates.length === 0}
              >
                <Plus aria-hidden />
                {t('optionsCard.addFirstOption')}
              </Button>
            ) : (
              <Button
                type="button"
                variant="link"
                size="xs"
                className="text-primary h-auto p-0 text-[13px]"
                disabled={!room || candidates.length === 0}
              >
                <Plus aria-hidden />
                {t('optionsCard.addOption')}
              </Button>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
            {candidates.map((attribute) => (
              <DropdownMenuItem key={attribute.id} onSelect={() => addOption(attribute.id)}>
                {attribute.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {suggestions.map((attribute) => (
          <button
            type="button"
            key={attribute.id}
            className="hover:bg-muted rounded-full border border-dashed px-2.5 py-0.5 text-[13px]"
            onClick={() => addOption(attribute.id)}
          >
            {t('optionsCard.suggest', { attribute: attribute.label })}
          </button>
        ))}
        {suggestions.length > 0 && productType.data ? (
          <span className="text-muted-foreground text-xs">
            {t('optionsCard.suggestedBy', { type: productType.data.name })}
          </span>
        ) : null}
      </div>
      {optionsError ? <p className="text-destructive text-xs">{optionsError}</p> : null}

      {creating ? (
        <>
          <FormField
            control={form.control}
            name="basePrice"
            render={({ field, fieldState }) => (
              <FormItem className="max-w-56">
                <FormLabel>{t('optionsCard.price')}</FormLabel>
                <FormControl>
                  <MoneyInput
                    currency={BASE_CURRENCY}
                    value={field.value ?? null}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="0.00"
                    aria-invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {variantsToCreate > 0 ? (
            <p
              role="status"
              data-testid="options-summary"
              className="text-muted-foreground text-[13px]"
            >
              {t('optionsCard.summary.onCreate', { count: variantsToCreate })}
            </p>
          ) : null}
        </>
      ) : saved ? (
        <SaveSummary
          saved={saved}
          options={options}
          attributesById={attributesById}
          variants={variants}
        />
      ) : null}
    </SummaryCard>
  );
}
